/*
 * Logs are emitted from both backend and frontend, so this formatter must stay
 * runtime-agnostic and cannot depend on Node-only utilities. We also normalize
 * values into serializable output here instead of passing raw objects through.
 */

const CONSOLE_METHODS = ['log', 'info', 'warn', 'error', 'debug', 'dir'] as const;
const CONSOLE_PATCHED = Symbol.for('lved.console.serialization.patched');

const stringifyValue = (value: unknown): string => {
    // Track the current ancestor chain to detect true circular references.
    const ancestors: object[] = [];

    const result: string = JSON.stringify(
        value,
        function (this: unknown, _key: string, currentValue: unknown) {
            if (currentValue instanceof Error) {
                return {
                    name: currentValue.name,
                    message: currentValue.message,
                    stack: currentValue.stack,
                    cause: currentValue.cause
                };
            }

            if (typeof currentValue === 'bigint') return `${currentValue.toString()}n`;

            if (typeof currentValue === 'function') return `[Function: ${currentValue.name || 'anonymous'}]`;

            if (typeof currentValue === 'symbol') return currentValue.toString();

            if (currentValue instanceof Map) {
                return {
                    type: 'Map',
                    entries: Array.from(currentValue.entries())
                };
            }

            if (currentValue instanceof Set) {
                return {
                    type: 'Set',
                    values: Array.from(currentValue.values())
                };
            }

            if (typeof currentValue === 'object' && currentValue !== null) {
                // Walk back up the ancestor stack: pop entries that are no
                // longer on the current serialization path. `this` is the
                // holder object whose property is being serialized.
                while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
                    ancestors.pop();
                }

                if (ancestors.includes(currentValue)) return '[Circular]';
                ancestors.push(currentValue);
            }

            return currentValue;
        },
        2
    );

    // JSON.stringify returns undefined for top-level undefined, functions, or symbols
    return result ?? String(value);
};

export const serializeLogValue = (value: unknown): unknown => {
    if (value === null || value === undefined) return value;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;

    if (value instanceof Error) return value.stack ?? `${value.name}: ${value.message}`;

    try {
        return stringifyValue(value);
    } catch {
        return String(value);
    }
};

export const installSerializedConsole = (): void => {
    const targetConsole = console as Console & { [CONSOLE_PATCHED]?: boolean };
    if (targetConsole[CONSOLE_PATCHED]) return;

    for (const method of CONSOLE_METHODS) {
        const originalMethod = targetConsole[method].bind(targetConsole);

        targetConsole[method] = (...args: unknown[]) => originalMethod(...args.map((arg) => serializeLogValue(arg)));
    }

    targetConsole[CONSOLE_PATCHED] = true;
};
